import {
  apiUrl,
  createPaymentRequest,
  getPosTerminals,
  initiateTerminalPayment,
  getTerminalPayment,
} from "@api";
import { toast } from "@components/toast/Toast";
import { useMerchant, usePaymentSettings } from "@auth";
import {
  Button,
  CurrencyInput,
  EmailInput,
  FileUploader,
  Flow,
  FlowActions,
  FlowHeader,
  FlowTitle,
  FormError,
  FormGroup,
  FormLabel,
  MobilePhoneInput,
  FlowBody,
  ProfilePaymentIcon,
  StyledSelect,
  SwitchInput,
  Tab,
  TabGroup,
  TextInput,
  SuccessIcon,
  FailIcon,
  ProcessingIcon,
  RequestSmsPreview,
  RequestEmailPreview,
} from "@components";
import { formatAmount, applyPaymentMethodRules } from "@utils";
import { useState, useEffect, useRef } from "react";
import {
  Calculator,
  ChatLeftDots,
  Envelope,
  Link45deg,
  LockFill,
  QrCode,
  Share,
  Whatsapp,
} from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import PrefilPaymentRequestSelect from "../components/PrefilPaymentRequestSelect";
import useUpdatePaymentRequest from "../hooks/useUpdatePaymentRequest";
import usePrefillPaymentRequest from "../hooks/usePrefillPaymentRequest";

const RequestAPayment = ({ isOpen, onCreated, onClose, updateRequestData }) => {
  const amountRef = useRef(null);
  const [paymentId, setPaymentId] = useState(null);
  const { merchant, hasModule } = useMerchant();
  const { paymentSettings } = usePaymentSettings();
  const [linkCopied, setLinkCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [qrShown, setQrShown] = useState(false);
  const [communicationMethods, setCommunicationMethods] = useState({
    //    sms: !!email ? !!phone : true,
    sms: true,
    email: false,
    qr: false,
    share: false,
    pos: false,
  });
  const [posTerminals, setPosTerminals] = useState([]);
  const [posSubmitted, setPosSubmitted] = useState(false);
  const [posSession, setPosSession] = useState(null);
  const [posError, setPosError] = useState(null);
  const posPollTimer = useRef(null);

  const isAdyen = paymentSettings?.cardPaymentProvider === "Adyen";
  const klarnaAccountEmail = paymentSettings?.klarnaAccountEmail;
  const paymentRequestSettings = paymentSettings?.paymentRequests;
  const merchantAllowOpenBanking =
    paymentRequestSettings?.allowOpenBanking ?? false;
  const merchantAllowCard = paymentRequestSettings?.allowCard ?? false;
  const merchantAllowAmex = paymentRequestSettings?.allowAmex ?? false;
  const merchantAllowKlarna = paymentRequestSettings?.allowKlarna ?? false;
  const showKlarna = isAdyen && !!klarnaAccountEmail;
  const klarnaShouldShow = showKlarna && merchantAllowKlarna;
  const posEnabled = isAdyen && !!paymentSettings?.terminals?.enabled;

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
    formState: { errors },
    clearErrors,
  } = useForm({
    defaultValues: {
      amount: "",
      phone: "",
      email: "",
      reference: "",
      allowOpenBanking: true,
      allowCardPayment: false, // Default to false, will be updated when merchant loads
      allowAmex: true,
      allowKlarnaPayment: false, // Default to false, will be updated when merchant loads
      customerName: "",
      subject: "Please make your payment",
      message: `Please make a payment of ${formatAmount(
        0,
      )} to us using the link below.`,
    },
  });

  const isWhatsAppTestMerchant =
    merchant?.id === "0d512f12-dabf-4b26-87a8-a0044499687c" ||
    merchant?.id === "38390ff3-2851-4d82-a4d0-fa89ba17e368";

  const radios = [
    isWhatsAppTestMerchant
      ? { name: "Whatsapp", value: "sms", icon: Whatsapp }
      : { name: "SMS", value: "sms", icon: ChatLeftDots },
    { name: "Email", value: "email", icon: Envelope },
    { name: "Share", value: "share", icon: Share },
    // POS-enabled merchants take counter payments on the terminal instead of via QR.
    ...(posEnabled
      ? [{ name: "POS Terminal", value: "pos", icon: Calculator }]
      : [{ name: "QR Code", value: "qr", icon: QrCode }]),
  ];

  const getCommMethod = () => {
    if (communicationMethods.share) return "Link";
    if (communicationMethods.qr) return "QRCode";
    if (communicationMethods.sms) return "Sms";
    if (communicationMethods.email) return "Email";
  };

  const handleCommsSelected = (option) => {
    let newOptions = null;

    newOptions = {
      sms: option === "sms",
      email: option === "email",
      qr: option === "qr",
      share: option === "share",
      pos: option === "pos",
    };

    if (newOptions !== communicationMethods) {
      clearErrors();
    }

    if (newOptions.qr || newOptions.share || newOptions.pos) {
      setShowPreview(false);
    }

    setCommunicationMethods(newOptions);
  };

  const amountValue = watch("amount");
  const message = watch("message");

  const onMethodToggle = (fieldName) => {
    const next = applyPaymentMethodRules(getValues(), fieldName, {
      cardKey: "allowCardPayment",
    });
    setValue("allowOpenBanking", next.allowOpenBanking);
    setValue("allowCardPayment", next.allowCardPayment);
    setValue("allowAmex", next.allowAmex);
  };

  // Update form values when merchant data loads
  useEffect(() => {
    if (merchant) {
      setValue("allowOpenBanking", merchantAllowOpenBanking);
      setValue("allowCardPayment", merchantAllowCard);
      setValue("allowAmex", merchantAllowAmex);
      setValue(
        "allowKlarnaPayment",
        (merchantAllowKlarna && klarnaShouldShow) ?? false,
      );

      // Update message with merchant name
      const currentMessage = watch("message");
      const newMessage = currentMessage.replace(
        /to us using the link below\./,
        `to ${merchant?.name || "us"} using the link below.`,
      );
      setValue("message", newMessage);
    }
  }, [merchant, paymentSettings, setValue, watch]);

  const { cancelIfNeeded } = useUpdatePaymentRequest({ isOpen, updateRequestData, setValue, handleCommsSelected });

  useEffect(() => {
    if (isOpen && posEnabled) {
      getPosTerminals().then(setPosTerminals);
    }
  }, [isOpen, posEnabled]);

  // Stop polling when the dialog unmounts; the payment continues server-side and
  // lands in the Transactions list either way.
  useEffect(() => () => clearTimeout(posPollTimer.current), []);

  const updateMessageAmount = (newAmount) => {
    const oldAmountStr = formatAmount(amountValue || 0);
    const separator = "\n\n";
    const separatorIdx = message.indexOf(separator);

    // If there's a prefix (e.g. cancellation line), only search within the body
    const prefix = separatorIdx >= 0 ? message.substring(0, separatorIdx + separator.length) : "";
    const body = separatorIdx >= 0 ? message.substring(separatorIdx + separator.length) : message;

    const money = body.indexOf(oldAmountStr);
    if (money < 0) return;

    const newBody =
      body.substring(0, money) +
      formatAmount(newAmount || 0) +
      body.substring(money + oldAmountStr.length);

    setValue("message", prefix + newBody);
  };

  // Prefilling from a past request switches the dialog to the tab matching how that
  // request was sent (SMS/email/share) — but a POS payment stays on the terminal, so
  // the tab is pinned while the POS tab is active.
  const handlePrefillCommsSelected = (option) => {
    if (communicationMethods.pos) return;
    handleCommsSelected(option);
  };

  const {
    selectedSalesOrderIds,
    resetSalesOrderSelection,
    handleRecentPaymentSelected,
    handleSalesOrderSelected,
    handleSalesOrderDeselected,
  } = usePrefillPaymentRequest({ setValue, watch, handleCommsSelected: handlePrefillCommsSelected, updateMessageAmount, amountRef });

  const copyLink = (key) => {
    navigator.clipboard.writeText(
      window.location.origin.replace("portal", "webpay") + "/pay/" + paymentId,
    );
    setLinkCopied(key);
  };

  const resetPosState = () => {
    clearTimeout(posPollTimer.current);
    setPosSubmitted(false);
    setPosSession(null);
    setPosError(null);
  };

  const resetForm = () => {
    setPaymentId(null);
    resetPosState();
    reset({
      amount: "",
      phone: "",
      reference: "",
      customerName: "",
      subject: "Please make your payment",
      message: `Please make a payment of ${formatAmount(0)} to ${
        merchant?.name || "us"
      } using the link below.`,
      allowOpenBanking: merchantAllowOpenBanking,
      allowCardPayment: merchantAllowCard,
      allowAmex: merchantAllowAmex,
      allowKlarnaPayment: (merchantAllowKlarna && klarnaShouldShow) ?? false,
    });
    setLinkCopied(false);
    resetSalesOrderSelection();
    setCommunicationMethods({
      sms: true,
      email: false,
      qr: false,
      share: false,
    });
  };

  async function onSubmit(formData) {
    setSubmitting(true);

    try {
      const data = await createPaymentRequest({
        amountRequested: formData.amount,
        invoicesRequested: null,
        allowAdditionalInvoices: null,
        invoicesRequestedAreMandatory: null,
        includePdfStatement: null,
        phone: formData.phone,
        email: formData.email,
        communicationMethod: getCommMethod(),
        reference: formData.reference,
        customerName: formData.customerName,
        customerId: null,
        subject: formData.subject,
        message: formData.message,
        emailBody: formData.message, // Don't distinguish for single request
        payerBaseUrl:
          window.location.origin.replace("portal", "webpay") + "/pay",
        files,
        allowCardPayment: formData.allowCardPayment,
        allowKlarnaPayment: formData.allowKlarnaPayment,
        allowOpenBankingPayment: formData.allowOpenBanking,
        allowAmexPayment: formData.allowAmex,
        messageTemplateName: null,
        batchId: null,
        sendAsPlainText: false,
        bypassInterceptor: false,
        salesOrderIds:
          selectedSalesOrderIds.length > 0 ? selectedSalesOrderIds : null,
        emailContentFormat: "HtmlSingleBlock",
      });

      setSubmitted(formData);
      setShowPreview(false);
      setPaymentId(data.id);
      toast({ description: updateRequestData ? `Created a new payment request for ${formatAmount(formData.amount)}` : `Created a payment request for ${formatAmount(formData.amount)}` });

      await cancelIfNeeded();
    } catch (error) {
      console.error("[RequestAPayment] Payment request failed", error);
    } finally {
      setSubmitting(false);
    }
    if (communicationMethods.qr) {
      setQrShown(true);
    }
    if (onCreated) {
      await onCreated();
    }
  }

  // How long the portal keeps polling before telling the user to check the
  // Transactions list. The terminal itself allows up to ~150s for the customer.
  const POS_POLL_TIMEOUT_MS = 200_000;
  const POS_POLL_INTERVAL_MS = 2_500;

  const isPosSessionPending = (session) =>
    !session || session.status === "Pending" || session.status === "InProgress";

  const pollPosSession = async (id, startedAt) => {
    try {
      const session = await getTerminalPayment(id);
      setPosSession(session);
      if (!isPosSessionPending(session)) {
        if (onCreated) {
          await onCreated();
        }
        return;
      }
    } catch (error) {
      // Transient poll failure — keep trying until the timeout.
      console.error("[RequestAPayment] Terminal payment poll failed", error);
    }

    if (Date.now() - startedAt > POS_POLL_TIMEOUT_MS) {
      setPosError(
        "The terminal is taking longer than expected. Check the Transactions list for the outcome.",
      );
      return;
    }

    posPollTimer.current = setTimeout(
      () => pollPosSession(id, startedAt),
      POS_POLL_INTERVAL_MS,
    );
  };

  async function onSubmitPos() {
    const valid = await trigger([
      "amount",
      "reference",
      "customerName",
      "posTerminal",
    ]);
    if (!valid) return;

    setSubmitting(true);
    setPosSubmitted(true);
    setPosSession(null);
    setPosError(null);

    try {
      const session = await initiateTerminalPayment({
        providerTerminalId: getValues("posTerminal"),
        amount: getValues("amount"),
        reference: getValues("reference"),
        customerName: getValues("customerName"),
      });
      setPosSession(session);
      posPollTimer.current = setTimeout(
        () => pollPosSession(session.id, Date.now()),
        POS_POLL_INTERVAL_MS,
      );
    } catch (error) {
      console.error("[RequestAPayment] Terminal payment failed", error);
      const message =
        error?.response?.status === 409
          ? "A payment is already in progress on this terminal."
          : "Could not send the payment to the terminal. Please try again.";
      setPosError(message);
      toast({ description: message });
    } finally {
      setSubmitting(false);
    }
  }

  function posResultBody() {
    const failed =
      posSession?.status === "Failed" || (!posSession && posError);
    const approved = posSession?.status === "Succeeded";
    const unknown = posSession?.status === "Unknown" || (posSession && posError);
    const pending = !failed && !approved && !unknown;
    const posAmount = getValues("amount");

    return (
      <>
        {pending ? (
          <ProcessingIcon className="mx-auto mt-12 mb-6" />
        ) : approved ? (
          <SuccessIcon className="mx-auto mt-12 mb-6" />
        ) : (
          <FailIcon className="mx-auto mt-12 mb-6" />
        )}
        <h1 className="text-2xl font-semibold text-center p-5 border-b border-zinc-100 mb-3">
          {pending
            ? "Sent to terminal"
            : approved
              ? "Payment approved"
              : unknown
                ? "Payment outcome unknown"
                : "Payment not completed"}
        </h1>
        <p className="text-center text-zinc-500 px-5">
          {pending
            ? `Waiting for the customer to pay ${formatAmount(posAmount)} on the terminal…`
            : approved
              ? `The customer paid ${formatAmount(posAmount)} on the terminal.`
              : unknown
                ? posError ||
                  "We could not confirm the outcome. Check the Transactions list before retrying."
                : posSession?.failureReason ||
                  posError ||
                  "The payment was declined or cancelled on the terminal."}
        </p>
        {!pending && (
          <div className="w-full flex gap-2 mt-10">
            {failed && (
              <Button color="link" onClick={() => resetPosState()}>
                Try again
              </Button>
            )}
            <Button className="ml-auto" onClick={() => handleClose()}>
              Close
            </Button>
          </div>
        )}
      </>
    );
  }

  function successBody() {
    if (communicationMethods.qr) {
      return (
        <>
          <img
            src={`${apiUrl()}/api/paymentrequests/${paymentId}/qrcode`}
            alt="Payment QR code"
            className="mx-auto mt-10"
            style={{ maxWidth: "80%" }}
          />
          <p className="text-center font-semibold">
            Scan the QR code to make a payment of{" "}
            {formatAmount(submitted.amount)}
          </p>
          <Button
            onClick={() => handleClose()}
            className="mx-auto mt-10 w-full"
          >
            Close
          </Button>
        </>
      );
    }
    if (
      communicationMethods.share ||
      communicationMethods.sms ||
      communicationMethods.email
    ) {
      return (
        <>
          <SuccessIcon className="mx-auto mt-12 mb-6" />
          <h1 className="text-2xl font-semibold text-center p-5 border-b border-zinc-100 mb-3">
            Payment link{" "}
            {communicationMethods.sms || communicationMethods.email
              ? "sent"
              : "created"}
          </h1>
          {updateRequestData && (
            <div className="w-full px-5 mb-1">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-2 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-200 w-full">
                <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-blue-800 text-[10px] font-bold leading-none text-white">
                  i
                </span>
                The previous payment link for {formatAmount(updateRequestData.amountRequested)} has been cancelled and replaced with the new payment link below.
              </span>
            </div>
          )}
          <div className="w-full p-5 border-b border-zinc-100">
            <div className="w-full py-3 px-3 bg-zinc-50 border border-zinc-200 text-zinc-400 relative overflow-hidden ">
              <a
                className="text-nowrap overflow-hidden"
                href={
                  window.location.origin.replace("portal", "webpay") +
                  "/pay/" +
                  paymentId
                }
              >
                {window.location.origin.replace("portal", "webpay") +
                  "/pay/" +
                  paymentId}
              </a>
              <Button
                disabled={linkCopied}
                className="absolute right-2 top-1.5"
                color="secondary"
                type="button"
                onClick={() => copyLink("top")}
              >
                <Link45deg size={16} className="me-1" />
                {linkCopied === "top" ? "Link copied" : "Copy link"}
              </Button>
            </div>
          </div>
          {submittedInfo(submitted)}
          <div className="w-full flex gap-2">
            {!updateRequestData && (
              <Button color="link" onClick={() => resetForm()}>
                Request another payment
              </Button>
            )}
            <Button className="ml-auto" onClick={() => handleClose()}>
              Close
            </Button>
            {communicationMethods.share && (
              <Button color="primary" onClick={() => copyLink("bottom")}>
                <Link45deg size={16} className="me-1" />
                {linkCopied === "bottom" ? "Link copied" : "Copy link"}
              </Button>
            )}
          </div>
        </>
      );
    }
    return <p>Success</p>;
  }

  const submittedInfo = (submitted) => (
    <div className="w-full p-5">
      <div className="w-full p-5 grid grid-cols-2 border-b border-zinc-100">
        <p className="m-0 font-medium">Amount</p>
        <p className="m-0 text-zinc-500">{formatAmount(submitted.amount)}</p>
      </div>
      <div className="w-full p-5 grid grid-cols-2 border-b border-zinc-100">
        <p className="m-0 font-medium">Reference</p>
        <p className="m-0 text-zinc-500">{submitted.reference}</p>
      </div>
      <div className="w-full p-5 grid grid-cols-2 border-b border-zinc-100">
        <p className="m-0 font-medium">Customer</p>
        <p className="m-0 text-zinc-500">{submitted.customerName}</p>
      </div>
      {submitted.phone && (
        <div className="w-full p-5 grid grid-cols-2 border-b border-zinc-100">
          <p className="m-0 font-medium">Customer mobile</p>
          <p className="m-0 text-zinc-500">{submitted.phone}</p>
        </div>
      )}
      {submitted.email && (
        <div className="w-full p-5 grid grid-cols-2 border-b border-zinc-100">
          <p className="m-0 font-medium">Customer email</p>
          <p className="m-0 text-zinc-500">{submitted.email}</p>
        </div>
      )}
    </div>
  );

  const handleClose = () => {
    if (submitting) {
      return;
    }
    resetForm();
    onClose();
  };

  const primaryColour = merchant?.customisationSettings?.colourSet?.primary;

  return (
    <Flow isOpen={isOpen} onClose={handleClose}>
      <FlowHeader onClose={handleClose}>
        <FlowTitle>{updateRequestData ? 'Update a payment request' : 'Request a payment'}</FlowTitle>
        <FlowActions>
          {!paymentId && !posSubmitted && communicationMethods.pos && (
            <Button
              color="primary"
              type="button"
              submitting={submitting}
              disabled={posTerminals.length === 0}
              onClick={onSubmitPos}
            >
              Send to terminal
            </Button>
          )}
          {!paymentId && !communicationMethods.pos && (
            <>
              <Button
                color="secondary"
                type="button"
                disabled={
                  communicationMethods.qr ||
                  communicationMethods.share ||
                  submitting
                }
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? "Hide preview" : "Show preview"}
              </Button>
              <Button
                color="primary"
                type="submit"
                submitting={submitting}
                onClick={handleSubmit(onSubmit)}
              >
                {updateRequestData ? 'Update payment request' : 'Create payment request'}
              </Button>
            </>
          )}
        </FlowActions>
      </FlowHeader>
      <FlowBody>
        <div className="flex w-full h-full">
          <div className={`h-full w-${showPreview ? "1/2" : "full"}`}>
            <div className={"flex flex-col mx-auto w-full max-w-[700px] px-8"}>
              {paymentId && submitted ? (
                successBody()
              ) : posSubmitted ? (
                posResultBody()
              ) : (
                <>
                  {updateRequestData && (
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-2 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-200 w-full mt-8">
                      <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-blue-800 text-[10px] font-bold leading-none text-white">
                        i
                      </span>
                      You're updating an existing payment request. Once created, the original request will be cancelled and replaced by this one.
                    </span>
                  )}
                  <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                  <TabGroup>
                    {radios.map((radio, idx) => (
                      <Tab
                        key={idx}
                        id={`radio-${idx}`}
                        title={radio.name}
                        value={radio.value}
                        active={communicationMethods[radio.value]}
                        onClick={() => handleCommsSelected(radio.value)}
                      >
                        <radio.icon className="mr-2" />
                        {radio.name}
                      </Tab>
                    ))}
                  </TabGroup>
                  {communicationMethods.pos && (
                    <div>
                      <FormGroup>
                        <FormLabel>Terminal</FormLabel>
                        {posTerminals.length === 0 ? (
                          <p className="text-sm text-zinc-500 m-0 py-2">
                            No terminals are available. Terminals are set up by
                            Ontik support.
                          </p>
                        ) : (
                          <StyledSelect
                            name="posTerminal"
                            control={control}
                            displayName="Terminal"
                            required={true}
                            options={posTerminals.map((t) => ({
                              key: t.providerTerminalId,
                              label: `${t.friendlyName} (${t.branch})`,
                            }))}
                          />
                        )}
                        <FormError name="posTerminal" errors={errors} />
                      </FormGroup>
                      <div className="border-b border-zinc-100 -mt-5 mb-2 pb-2" />
                    </div>
                  )}
                  <div>
                    <FormGroup>
                      <FormLabel>{hasModule("SalesOrdersV2") ? "Prefill details from Quotes, Sales orders & Past requests" : "Prefill from past requests"}</FormLabel>
                      <PrefilPaymentRequestSelect
                        onSelected={(x) => handleSalesOrderSelected(x)}
                        onDeselected={(x) => handleSalesOrderDeselected(x)}
                        onPaymentRequestSelected={handleRecentPaymentSelected}
                      />
                    </FormGroup>
                    <div className="border-b border-zinc-100 -mt-5 mb-2 pb-2"/>
                  </div>

                  <div ref={amountRef}>
                    <FormGroup>
                      <FormLabel>Amount</FormLabel>
                      <CurrencyInput
                        name="amount"
                        control={control}
                        displayName="Amount"
                        required={true}
                        min={0.3}
                        onChange={(x) => updateMessageAmount(x)}
                        errors={errors.amount}
                      />
                      <FormError name="amount" errors={errors} />
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <FormLabel>Reference</FormLabel>
                    <TextInput
                      displayName="Reference"
                      control={control}
                      required={true}
                      name="reference"
                      placeholder="INV100001"
                      errors={errors.reference}
                    />
                    <FormError name="reference" errors={errors} />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Customer name</FormLabel>
                    <TextInput
                      displayName="Customer name"
                      control={control}
                      required={true}
                      name="customerName"
                      errors={errors.customerName}
                    />
                    <FormError name="customerName" errors={errors} />
                  </FormGroup>
                  {communicationMethods.sms && (
                    <FormGroup>
                      <FormLabel>Customer mobile</FormLabel>
                      <MobilePhoneInput
                        name="phone"
                        control={control}
                        displayName="Customer mobile"
                        errors={errors.phone}
                        required={true}
                      />
                      <FormError name="phone" errors={errors} />
                    </FormGroup>
                  )}
                  {communicationMethods.email && (
                    <FormGroup>
                      <FormLabel>Customer email</FormLabel>
                      <EmailInput
                        name="email"
                        control={control}
                        displayName="Customer email"
                        errors={errors.email}
                        required={true}
                        placeholder="your@customer.co.uk"
                      />
                      <FormError name="email" errors={errors} />
                    </FormGroup>
                  )}
                  {communicationMethods.email && (
                    <FormGroup>
                      <FormLabel>Subject</FormLabel>
                      <TextInput
                        name="subject"
                        info="The text you enter here will appear in the email subject line."
                        control={control}
                        displayName="Subject"
                        errors={errors.subject}
                        required={true}
                      />
                      <FormError name="subject" errors={errors} />
                    </FormGroup>
                  )}
                  {!isWhatsAppTestMerchant &&
                    (communicationMethods.sms ||
                      communicationMethods.email) && (
                      <FormGroup>
                        <FormLabel>Message</FormLabel>
                        <TextInput
                          name="message"
                          control={control}
                          displayName="Message"
                          errors={errors.message}
                          required={true}
                          area={true}
                          rows={!!updateRequestData ? 4 : 2}
                        />
                        <FormError name="message" errors={errors} />
                      </FormGroup>
                    )}
                  {!communicationMethods.pos && (
                    <FormGroup>
                      <FormLabel>Attachments</FormLabel>
                      <FileUploader
                        name="files"
                        maxFiles={200}
                        onChange={setFiles}
                      />
                    </FormGroup>
                  )}
                  {!communicationMethods.pos && (
                  <FormGroup className="border-t pt-6">
                    {[
                      {
                        id: "allowOpenBanking",
                        name: "Pay by Bank",
                        icon: "Bank",
                        fieldName: "allowOpenBanking",
                        disabled:
                          !merchantAllowOpenBanking || !merchantAllowCard,
                      },
                      {
                        id: "allowCards",
                        name: "Personal / Corporate Card",
                        icon: "Card",
                        fieldName: "allowCardPayment",
                        disabled:
                          !merchantAllowCard || !merchantAllowOpenBanking,
                      },
                      {
                        id: "allowAmex",
                        name: "American Express",
                        icon: "Amex",
                        fieldName: "allowAmex",
                        disabled: !merchantAllowAmex,
                      },
                      ...(showKlarna
                        ? [
                            {
                              id: "klarna",
                              name: "Klarna",
                              icon: "Klarna",
                              fieldName: "allowKlarnaPayment",
                              disabled: !merchantAllowKlarna,
                            },
                          ]
                        : []),
                    ].map((method) => {
                      const isToggleOn = watch(method.fieldName);
                      return (
                        <div
                          key={method.id}
                          className="flex items-center justify-between py-2"
                        >
                          <div className="flex items-center gap-6">
                            <div className={method.disabled ? "opacity-40" : undefined}>
                              <ProfilePaymentIcon paymentType={method.icon} />
                            </div>
                            <div className="flex flex-col">
                              <span className={`text-sm font-medium ${method.disabled ? "text-zinc-400" : "text-zinc-900"}`}>
                                {method.name}
                              </span>
                              {method.disabled && !isToggleOn && (
                                <span className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5">
                                  <LockFill className="size-3" />
                                  Disabled by admin
                                </span>
                              )}
                            </div>
                          </div>
                          {(!method.disabled || isToggleOn) && (
                            <SwitchInput
                              name={method.fieldName}
                              control={control}
                              disabled={method.disabled}
                              onChange={
                                method.fieldName === "allowKlarnaPayment"
                                  ? undefined
                                  : () => onMethodToggle(method.fieldName)
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </FormGroup>
                  )}
                </form>
                </>
              )}
            </div>
          </div>
          <div
            className={`h-full flex flex-col bg-zinc-50 w-${
              showPreview ? "1/2" : "0"
            }`}
          >
            {showPreview && communicationMethods.sms && (
              <div className="w-[390px] max-w-[390px] mx-auto flex-1 p-8">
                <RequestSmsPreview
                  message={watch("message")}
                  completeMessage={watch("message")}
                  amount={watch("amount")}
                />
              </div>
            )}
            {showPreview && communicationMethods.email && (
              <div className="w-full px-auto flex-1 relative z-0">
                <RequestEmailPreview
                  customerName={watch("customerName")}
                  accountCode={watch("reference")}
                  isPlainText={false}
                  completeMessage={watch("message")}
                  amount={watch("amount")}
                />
              </div>
            )}
          </div>
        </div>
      </FlowBody>
    </Flow>
  );
};

export default RequestAPayment;
